import { BellIcon } from "lucide-react";

import { Label } from "@/components/ui/label";
import type { apiClient } from "../../../../lib/api";
import { DELAY_OPTIONS } from "../../constants";
import type { GoalFormState } from "../../types";
import { SegmentButton } from "../SegmentButton";

type Circle = Awaited<ReturnType<typeof apiClient.getCircles>>[number];

interface AccountabilitySectionProps {
  readonly circles: Circle[] | undefined;
  readonly form: GoalFormState;
  readonly setForm: (value: GoalFormState | ((previous: GoalFormState) => GoalFormState)) => void;
  readonly toggleSharedCircle: (circleId: string) => void;
}

export function AccountabilitySection({
  circles,
  form,
  setForm,
  toggleSharedCircle,
}: AccountabilitySectionProps) {
  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-3.5">
      <div className="flex items-center gap-2">
        <BellIcon className="w-4 h-4 text-primary" />
        <Label className="font-medium">Circle accountability</Label>
      </div>

      {!circles || circles.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Create a circle first to let friends know when you miss this goal.
        </p>
      ) : (
        <>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Tell this circle when I miss:</p>
            <div className="flex gap-1.5 flex-wrap">
              <SegmentButton
                active={form.notifyCircleId === null}
                onClick={() => setForm((previous) => ({ ...previous, notifyCircleId: null }))}
              >
                No one
              </SegmentButton>
              {circles.map((circle) => (
                <SegmentButton
                  key={circle.id}
                  active={form.notifyCircleId === circle.id}
                  onClick={() => setForm((previous) => ({ ...previous, notifyCircleId: circle.id }))}
                >
                  {circle.name}
                </SegmentButton>
              ))}
            </div>
          </div>

          {form.notifyCircleId && (
            <>
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Notify after</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[1, 2, 3, 4, 5].map((count) => (
                    <SegmentButton
                      key={count}
                      active={form.notifyThresholdCount === count}
                      onClick={() =>
                        setForm((previous) => ({ ...previous, notifyThresholdCount: count }))
                      }
                    >
                      {count} miss{count !== 1 ? "es" : ""}
                    </SegmentButton>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Within</p>
                <div className="flex gap-1.5">
                  <SegmentButton
                    active={form.notifyThresholdPeriod === "DAY"}
                    onClick={() =>
                      setForm((previous) => ({ ...previous, notifyThresholdPeriod: "DAY" }))
                    }
                  >
                    One day
                  </SegmentButton>
                  <SegmentButton
                    active={form.notifyThresholdPeriod === "WEEK"}
                    onClick={() =>
                      setForm((previous) => ({ ...previous, notifyThresholdPeriod: "WEEK" }))
                    }
                  >
                    One week
                  </SegmentButton>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  How soon after the due time should they hear about it?
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {DELAY_OPTIONS.map((option) => (
                    <SegmentButton
                      key={option.value}
                      active={form.notifyDelayMinutes === option.value}
                      onClick={() =>
                        setForm((previous) => ({
                          ...previous,
                          notifyDelayMinutes: option.value,
                        }))
                      }
                    >
                      {option.label}
                    </SegmentButton>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground/80 leading-relaxed">
                  Example: a 10:00 AM slot with a 1 hour delay notifies your circle by 11:00 AM.
                </p>
              </div>
            </>
          )}

          <div className="space-y-1.5 pt-1 border-t border-border/60">
            <p className="text-xs text-muted-foreground pt-2">Share day-to-day progress with:</p>
            <div className="flex gap-1.5 flex-wrap">
              {circles.map((circle) => (
                <SegmentButton
                  key={circle.id}
                  active={form.sharedCircleIds.includes(circle.id)}
                  onClick={() => toggleSharedCircle(circle.id)}
                >
                  {circle.name}
                </SegmentButton>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
